import { parseDate, stringifyDate } from '@superdispatch/dates';
import { Typography } from '@material-ui/core';
import { FormikDateField, FormikDateFieldProps } from '@superdispatch/forms';
import { ColorDynamic } from '@superdispatch/ui';
import { getIn, useFormikContext } from 'formik';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useToday } from 'shared/hooks/useToday';
import styled from 'styled-components';
import { PostingLoadDTO } from '../../data/dto/PostingLoadDTO';
import { PostingRequestValuesDTO } from '../../data/dto/PostingRequestDTO';
import {
  getDateFieldRules,
  getDefaultPickerTime,
  setDefaultPickerHour,
  StepKey,
} from './postingDateRules';

interface RequestDateFieldProps extends FormikDateFieldProps {
  stepKey: StepKey;
  load: PostingLoadDTO;
  isBooking: boolean;
}

const RuleRow = styled.div`
  display: flex;
  align-items: flex-start;
`;

const RuleBox = styled.span`
  flex: none;
  width: 16px;
  height: 16px;
  margin: 2px 6px 0 0;
  border-radius: 2px;
  background-color: ${ColorDynamic.Blue50};
`;

export function RequestDateField({
  name,
  stepKey,
  load,
  isBooking,
  disabled,
  validate,
  onBlur,
  ...props
}: RequestDateFieldProps) {
  const { values, initialValues, setFieldValue, setFieldTouched } =
    useFormikContext<PostingRequestValuesDTO>();
  const today = useToday();
  const pickupValue = values.pickup_date;
  const rules = useMemo(
    () =>
      getDateFieldRules(stepKey, {
        load,
        isBooking,
        today,
        pickupDate: parseFieldDate(pickupValue),
      }),
    [stepKey, load, isBooking, today, pickupValue],
  );
  const { lockedDate, footerRule, tintRange } = rules;
  const isPickupLocked = useMemo(
    () =>
      stepKey === 'delivery' &&
      !!getDateFieldRules('pickup', { load, isBooking, today }).lockedDate,
    [stepKey, load, isBooking, today],
  );
  const [disabledReason, setDisabledReason] = useState<string | null>(null);
  const [isCleared, setCleared] = useState(false);
  const value: unknown = getIn(values, name);

  const initialTime = useMemo(
    () => (getIn(initialValues, name) ? undefined : getDefaultPickerTime()),
    [initialValues, name],
  );

  // A locked Exact field always shows the shipper's date, even on an
  // existing request that was saved with a different one.
  useEffect(() => {
    if (!lockedDate) return;

    const current = parseFieldDate(value);
    if (current.isValid && current.hasSame(lockedDate, 'day')) return;

    void setFieldValue(
      name,
      stringifyDate(setDefaultPickerHour(lockedDate), { format: 'JodaISO' }),
    );
  }, [lockedDate, name, setFieldValue, value]);

  // Moving pickup past the chosen delivery leaves no valid delivery: clear it.
  // Only a pickup change counts, so a saved request opens as it was saved.
  const previousPickup = useRef(pickupValue);
  useEffect(() => {
    const hasPickupChanged = previousPickup.current !== pickupValue;
    previousPickup.current = pickupValue;
    if (!hasPickupChanged || stepKey !== 'delivery' || lockedDate) return;

    const pickup = parseFieldDate(pickupValue);
    const delivery = parseFieldDate(value);
    if (
      pickup.isValid &&
      delivery.isValid &&
      delivery < pickup.startOf('day')
    ) {
      void setFieldValue(name, null);
      // Untouched, so "required" doesn't hide the guidance until submit.
      void setFieldTouched(name, false, false);
      setCleared(true);
    }
  }, [
    stepKey,
    lockedDate,
    pickupValue,
    value,
    name,
    setFieldValue,
    setFieldTouched,
  ]);

  const showClearedHint = isCleared && !value;

  function getHelperText() {
    if (showClearedHint) {
      return isPickupLocked
        ? 'Pickup set by shipper. Select a new delivery date.'
        : 'Pickup changed. Select a new delivery date.';
    }
    if (lockedDate) return 'Set by shipper';
    return undefined;
  }

  return (
    <FormikDateField
      {...props}
      name={name}
      format="JodaISO"
      placeholder="Select Date"
      validate={(info) =>
        validate?.(info) ?? rules.getDeadlineError(info.dateValue) ?? undefined
      }
      disabled={disabled || !!lockedDate}
      onBlur={() => {
        setDisabledReason(null);
        onBlur?.();
      }}
      helperText={getHelperText()}
      CalendarProps={{
        initialTime,
        disabledDays: ({ dateValue }) =>
          !!rules.getDisabledReason(dateValue.startOf('day')),
        highlightedDays: {
          blue: tintRange
            ? ({ dateValue }) =>
                dateValue >= tintRange[0] && dateValue <= tintRange[1]
            : undefined,
        },
        onDayClick: ({ dateValue }) => {
          setDisabledReason(rules.getDisabledReason(dateValue.startOf('day')));
        },
      }}
      renderFooter={() => (
        <div>
          {footerRule && (
            <RuleRow>
              {footerRule.hasBlueBox && <RuleBox />}
              <Typography color="textSecondary">{footerRule.text}</Typography>
            </RuleRow>
          )}
          <div role="status" aria-live="polite">
            {disabledReason && (
              <Typography variant="caption" color="textSecondary">
                {disabledReason}
              </Typography>
            )}
          </div>
        </div>
      )}
    />
  );
}

function parseFieldDate(value: unknown) {
  return parseDate(typeof value === 'string' ? value : null, {
    format: 'JodaISO',
  });
}
